import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronRight, Filter, Search, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import {
  categoryLabel,
  collectionCategories,
  collectionPath,
  commercialModeLabel,
  formatEuro,
  intentOptions,
  levelOptions,
  liveDropProducts,
  matchConfidenceLabel,
  productMatchesIntent
} from '../data/liveDrop';
import { getProductLaunchContent } from '../data/productContent';
import { CategoryChoiceBlocks } from './CategoryChoiceBlocks';
import { SetBuilder } from './SetBuilder';
import { trackEvent } from '../lib/analytics';

type ProductExplorerPageProps = {
  initialCategory?: string | null;
};

export function ProductExplorerPage({ initialCategory = null }: ProductExplorerPageProps) {
  const [category, setCategory] = useState(initialCategory ?? 'alles');
  const [level, setLevel] = useState('alles');
  const [intent, setIntent] = useState('alles');
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.title = initialCategory ? `${categoryLabel(initialCategory)} | ProPickleball.nl` : 'Producten | ProPickleball.nl';
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content = 'Bekijk de collectie in voorbereiding van ProPickleball.nl met keuzehulp, prijsindicaties en beschikbaarheidsupdates.';
    }
  }, [initialCategory]);

  useEffect(() => {
    setCategory(initialCategory ?? 'alles');
  }, [initialCategory]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return liveDropProducts.filter((product) => {
      const matchesCategory = category === 'alles' || product.category === category;
      const matchesLevel = level === 'alles' || product.level === level;
      const matchesIntent = productMatchesIntent(product, intent);
      const matchesQuery = !normalizedQuery || `${product.name} ${product.brand} ${product.category} ${product.audience}`.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesLevel && matchesIntent && matchesQuery;
    });
  }, [category, intent, level, query]);

  useEffect(() => {
    if (filteredProducts.length === 0) {
      trackEvent({ event: 'no_result_view', location: 'product_explorer', label: `${category}:${level}:${intent}`, routeFamily: 'products' });
    }
  }, [category, filteredProducts.length, intent, level]);

  function updateCategory(value: string) {
    setCategory(value);
    trackEvent({ event: 'collection_filter_click', location: 'product_explorer', label: value, category: value, routeFamily: 'products' });
  }

  function updateLevel(value: string) {
    setLevel(value);
    trackEvent({ event: 'collection_filter_click', location: 'product_explorer', label: value, routeFamily: 'products' });
  }

  function updateIntent(value: string) {
    setIntent(value);
    trackEvent({ event: 'collection_filter_click', location: 'product_explorer', label: value, routeFamily: 'products' });
  }

  return (
    <>
      <main className="productExplorerPage">
        <nav className="productTopbar" aria-label="Productoverzicht navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/"><ArrowLeft size={18} /> Terug naar home</a>
        </nav>
        <nav className="seoBreadcrumbs productBreadcrumbs" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <ChevronRight size={15} aria-hidden="true" />
          <a href="/producten/">Producten</a>
          {initialCategory ? (
            <>
              <ChevronRight size={15} aria-hidden="true" />
              <span>{categoryLabel(initialCategory)}</span>
            </>
          ) : null}
        </nav>

        <section className="explorerHero">
          <p className="eyebrow">Producten</p>
          <h1>{initialCategory ? categoryLabel(initialCategory) : 'Shop de eerste pickleball collectie.'}</h1>
          <p>
            Filter de eerste selectie op categorie, niveau en speelsituatie. Producten zijn nog niet direct te bestellen; je kunt wel alvast een update of pakketadvies aanvragen.
          </p>
          <div className="explorerTrust">
            <span><ShieldCheck size={16} /> Eerlijk over beschikbaarheid</span>
            <span><ShieldCheck size={16} /> Prijsindicatie per product</span>
            <span><ShieldCheck size={16} /> Advies voor NL/BE spelers</span>
          </div>
        </section>

        <CategoryChoiceBlocks category={initialCategory ?? 'overview'} />
        {initialCategory === 'startersets' ? <SetBuilder /> : null}

        <section className="explorerShell" aria-label="Productfilters">
          <aside className="filterRail">
            <div className="filterTitle"><SlidersHorizontal /><strong>Filters</strong></div>
            <label>
              Zoek product
              <span className="searchInput"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Paddle, indoor, club..." /></span>
            </label>
            <label>
              Categorie
              <select value={category} onChange={(event) => updateCategory(event.target.value)}>
                <option value="alles">Alle categorieen</option>
                {collectionCategories.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
            <label>
              Niveau
              <select value={level} onChange={(event) => updateLevel(event.target.value)}>
                {levelOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
            <label>
              Doel
              <select value={intent} onChange={(event) => updateIntent(event.target.value)}>
                {intentOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
          </aside>

          <div className="explorerResults">
            <div className="resultsTopline">
              <div><Filter size={18} /> {filteredProducts.length} producten gevonden</div>
              <a href="/keuzehulp/">Start keuzehulp <ArrowRight size={16} /></a>
            </div>

            {filteredProducts.length === 0 ? (
              <article className="emptyResults">
                <h2>Geen match in de huidige collectie.</h2>
                <p>Reset de filters of start de keuzehulp. Deze lege staat wordt gemeten zonder persoonsgegevens.</p>
                <button type="button" onClick={() => { setCategory('alles'); setLevel('alles'); setIntent('alles'); setQuery(''); }}>Reset filters</button>
              </article>
            ) : (
              <div className="explorerGrid">
                {filteredProducts.map((product) => {
                  const content = getProductLaunchContent(product.sku);
                  return (
                    <a
                      className="explorerCard"
                      href={product.pdpPath}
                      key={product.sku}
                      onClick={() => trackEvent({ event: 'select_item', location: 'product_explorer', label: product.name, sku: product.sku, category: product.category, brand: product.brand, matchType: product.marketResearch.benchmark.matchType, routeFamily: 'products' })}
                    >
                      <img className="explorerCardImage" src={content.image} alt="" loading="lazy" />
                      <span>{categoryLabel(product.category)} - eerste batch volgt</span>
                      <h2>{product.name}</h2>
                      <p>{content.headline}</p>
                      <dl>
                        <div><dt>Richtprijs</dt><dd>{formatEuro(product.marketResearch.targetPriceInclVat)}</dd></div>
                        <div><dt>Status</dt><dd>{commercialModeLabel(product.commercialMode)}</dd></div>
                        <div><dt>Prijsbasis</dt><dd>{matchConfidenceLabel(product.marketResearch.benchmark.matchType)}</dd></div>
                      </dl>
                      <strong className="explorerCardCta">Bekijk product <ArrowRight size={16} /></strong>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="collectionLinkBand" aria-label="Collecties">
          {collectionCategories.map((option) => (
            <a href={collectionPath(option.key)} key={option.key}>{option.label}<span>{option.count}</span></a>
          ))}
        </section>
      </main>
      <LegalFooter />
    </>
  );
}
